import { Component, Input, OnChanges, OnInit, signal, SimpleChanges, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { TimeUnit } from '../../../../../shared/components/date-picker/date-picker.component';
import { ApiService } from '../../../../../core/services/api.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { IconComponent } from '../../../../../shared/icons/icon.component';
import { ToggleBtnsComponent } from '../../../../../shared/components/toggle-btns/toggle-btns.component';
import { ClickOutsideDirective } from "../../../../../shared/directives/click-outside.directive";
import { LineChartComponent } from '../../../../../shared/components/charts/line-chart/line-chart.component';
import { ScrollTableComponent } from '../../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { IndividualToasterComponent } from "../../../../../shared/components/loader/loader.component";

// Enable UTC plugin
dayjs.extend(utc);

interface TableHeader {
  label: string;
  key: string;
  align?: 'text-left' | 'text-right' | 'text-center';
  isPrevious?: boolean;
  showProgress?: boolean;
  showTooltip?: boolean;
  showIcon?: boolean;
  noTransform?: boolean;
}

@Component({
  selector: 'app-contract-count-stats',
  imports: [CommonModule, IconComponent, ToggleBtnsComponent, LineChartComponent, ScrollTableComponent, IndividualToasterComponent],
  templateUrl: './contract-count-stats.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class ContractCountStatsComponent implements OnInit, OnChanges {
  isLoading = signal(false)
  @Input() productServerName: string = 'denefits'; // Dynamic product server name
  @Input() requiresAccessToken: boolean = false; // Whether the product requires an access token

  @Input() cocu_id: number = 1;
  @Input() country_id: number = 1;

  @Input() dateRange: { start_date: string, end_date: string, previous_start_date: string | null, previous_end_date: string | null, unit: TimeUnit; } = {
    start_date: '',
    end_date: '',
    previous_start_date: null,
    previous_end_date: null,
    unit: 'day'
  };

  contracts = {
    hoveredType: undefined,
    loading: false,
    chart_value_type: 1,
    contractsToday: { 
      loading: false,
      contract_type_count: [] as any[],
      total_contracts: 0,
    },
    contractsData: [],
    previouscontractsData: [],
    stats: {},
    previousstats: {},
    current_data: {},
  };

  dummyStats = {
    total: 10,
    dental: 2,
    healthcare: 5,
    non_healthcare: 3
  };

  barData: any[] = [];
  cardsData: any = {};
  tableHeaders: TableHeader[] = [];
  public viewSignal: WritableSignal<'chart' | 'table'> = signal('chart');
  showCurrentTooltip = false;
  showPreviousTooltip = false;
  
  // Tooltip properties
  tooltipVisible = false;
  hoveredRowData: any = null;
  tooltipPosition = { x: 0, y: 0 };

  constructor(
    private apiService: ApiService
  ) { }

  ngOnInit(): void {
    this.contracts.contractsToday = {...this.contracts.contractsToday, ...this.dummyStats};
    this.FetchContractsToday();
    this.updateTableHeaders();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detect changes to dateRange, productServerName, cocu_id or country_id inputs
    if (changes['dateRange'] || 
        (changes['productServerName'] && !changes['productServerName'].firstChange) ||
        changes['cocu_id'] || 
        changes['country_id']) {
      
      // Always update table headers
      this.updateTableHeaders();
      
      // Check if previous dates are selected
      const hasPreviousDates = this.dateRange?.previous_start_date && this.dateRange?.previous_end_date;
      
      if (hasPreviousDates) {
        // Initialize previous data structure before fetching
        this.initializePreviousData();
        // Fetch both current and previous data
        this.FetchComparisonData();
      } else {
        // Clear previous data and fetch only current
        this.clearPreviousData();
        this.FetchTotalContracts(false, true, false);
      }
      
      // Also fetch today's stats when currency or product changes
      if (changes['cocu_id'] || changes['country_id'] || changes['productServerName']) {
        this.FetchContractsToday();
      }
    }
  }

  FetchContractsToday(useCache?: boolean, useLoader = false, isPrevious = false) {
    this.fetchContractData(
      '/visionboard/stats/today_contracts_new',
      'contractsToday',
      isPrevious,
      useCache,
      useLoader
    );
  }

  FetchTotalContracts(useCache?: boolean, useLoader = false, isPrevious = false) {
    this.fetchContractData(
      '/visionboard/stats/range_contracts',
      'totalContracts',
      isPrevious,
      useCache,
      useLoader
    );
  }

  private fetchContractData(
    apiKey: string,
    stateKey: string,
    isPrevious = false,
    useCache?: boolean,
    useLoader = false
  ) {
    const {
      start_date,
      end_date,
      previous_start_date,
      previous_end_date,
      unit,
    } = this.dateRange;
this.isLoading.set(true)
    const payload: any = {
      start_time: isPrevious
        ? dayjs(previous_start_date || new Date()).format('YYYY-MM-DD')
        : dayjs(start_date || new Date()).format('YYYY-MM-DD'),
      end_time: isPrevious
        ? dayjs(previous_end_date || new Date()).format('YYYY-MM-DD')
        : dayjs(end_date || new Date()).format('YYYY-MM-DD'),
      unit: unit || 'day',
      is_dummy: 0,
      cocu_id: this.cocu_id,
      country_id: this.country_id,
      // timezone: 'PST',
    };

    if (stateKey === 'contractsToday') {
      this.contracts.contractsToday = {
        loading: true,
        contract_type_count: [],
        total_contracts: 0
      };
    } else {
      if (isPrevious) {
        this.contracts.previouscontractsData = [];
        this.cardsData['previousChartData'] = [];
        this.cardsData['previousTableData'] = [];
      } else {
        this.contracts.contractsData = [];
        this.cardsData['chartData'] = [];
        this.cardsData['tableData'] = [];
      }
    }

    if (useLoader) {
      if (stateKey === 'contractsToday')
        this.contracts.contractsToday.loading = true;
      else this.contracts.loading = true;
    }

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }

    this.apiService.post(apiKey, payload, {
      addAccessToken: this.requiresAccessToken,
      productServerName: this.productServerName,
      showErrorToaster: true,
      showSuccessToaster: false,
      showLoader: false,
    }).subscribe({
      next: (value: any) => {
        this.isLoading.set(false)
        if (stateKey === 'contractsToday') {
          this.contracts.contractsToday = { contract_type_count:  [...value.contract_type_count], loading: false, total_contracts: value.total_contracts || 0 };
        } else {
          value?.records?.records?.map((record: any) => {
            record.start = dayjs.utc(record.start).format('YYYY-MM-DD');
            record.end = dayjs.utc(record.end).format('YYYY-MM-DD');
            return record;
          });
          const prefix = isPrevious ? 'previous' : '';
          this.contracts[`${prefix}contractsData`] = value.records.records || [];
          this.contracts[`${prefix}stats`] = value.records.stats || {};
          this.contracts.loading = false;

          if (!isPrevious) {
            this.cardsData['chartData'] = (value.records.records || []).map((record: any) => ({
              ...record,
              key: 'Current Contracts'
            }));
            this.cardsData['tableData'] = this.formatTableDates(value.records.records || [], this.dateRange.unit);
            
            // Preserve existing previous data when updating current data
            const existingPreviousData = this.cardsData['summary'] ? {
              previous: this.cardsData['summary'].previous,
              previous_financed: this.cardsData['summary'].previous_financed,
              previous_service_amount: this.cardsData['summary'].previous_service_amount,
              previous_downPayment_amount: this.cardsData['summary'].previous_downPayment_amount,
            } : {};
            
            this.cardsData['summary'] = {
              ...existingPreviousData,
              current: value.records.stats['total_contracts'] || 0,
              allTime: value.records.stats['total_contracts'] || 0,
              financed: value.records.stats['total_financed_amount'] || 0,
              service_amount: value.records.stats['total_treatment_amount'] || 0,
              downPayment_amount: value.records.stats['total_downpayment_amount'] || 0,
            };
            this.cardsData['allTimeHeading'] = 'All Time Total Contracts';
            
            // Only clear previous data if we're not expecting previous dates
            if (!this.dateRange.previous_start_date || !this.dateRange.previous_end_date) {
              this.cardsData['previousChartData'] = [];
              this.cardsData['previousTableData'] = [];
              // Remove previous fields from summary
              delete this.cardsData['summary'].previous;
              delete this.cardsData['summary'].previous_financed;
              delete this.cardsData['summary'].previous_service_amount;
              delete this.cardsData['summary'].previous_downPayment_amount;
            }
          } else {
            this.cardsData['previousChartData'] = (value.records.records || []).map((record: any) => ({
              ...record,
              key: 'Previous Contracts'
            }));
            this.cardsData['previousTableData'] = this.formatTableDates(value.records.records || [], this.dateRange.unit);
            this.cardsData['summary'] = {
              ...this.cardsData['summary'],
              previous: value.records.stats['total_contracts'] || 0,
              previous_financed: value.records.stats['total_financed_amount'] || 0,
              previous_service_amount: value.records.stats['total_treatment_amount'] || 0,
              previous_downPayment_amount: value.records.stats['total_downpayment_amount'] || 0,
            };
          }
          
          // Always recreate chart after data updates
          this.createMainChart();
        }
      },
      error: (error: any) => {
        this.isLoading.set(false)
        console.error('Denefits API Error:', error);
        if (stateKey === 'contractsToday') {
          this.contracts.contractsToday.loading = false;
        } else {
          this.contracts.loading = false;
        }
      }
    });
  }

  private createMainChart() {
    const valueKeys: { [key: number]: string; default: string } = {
      1: 'total_contracts',
      2: 'service_amount',
      3: 'downPayment_amount',
      4: 'financed_amount',
      default: 'total_contracts',
    };

    const key = valueKeys[this.contracts.chart_value_type] || valueKeys.default;
    const mapData = (arr: any[], labelSuffix = '') =>
      arr.map((d) => ({
        ...d,
        value: d[key],
        tooltip: `${d[key]} Contracts${labelSuffix}`,
        label: d.start,
        tick_start: d.start,
        tick_end: d.start,
      }));

    this.barData = [
      mapData(this.cardsData.chartData || []),
      mapData(this.cardsData.previousChartData || [], ' Previous Year'),
    ];
  }

  FetchComparisonData() {
    this.FetchTotalContracts(false, true, false);
    this.FetchTotalContracts(false, true, true);
  }

  private initializePreviousData(): void {
    // Initialize previous data structure to ensure clean state
    this.cardsData['previousChartData'] = [];
    this.cardsData['previousTableData'] = [];
    this.contracts.previouscontractsData = [];
    this.contracts.previousstats = {};
    
    // Initialize previous summary with default values
    if (!this.cardsData['summary']) {
      this.cardsData['summary'] = {};
    }
    
    this.cardsData['summary'] = {
      ...this.cardsData['summary'],
      previous: 0,
      previous_financed: 0,
      previous_service_amount: 0,
      previous_downPayment_amount: 0,
    };
  }

  private clearPreviousData(): void {
    // Clear previous chart and table data
    this.cardsData['previousChartData'] = [];
    this.cardsData['previousTableData'] = [];
    this.contracts.previouscontractsData = [];
    this.contracts.previousstats = {};
    
    // Remove previous summary data
    if (this.cardsData['summary']) {
      delete this.cardsData['summary'].previous;
      delete this.cardsData['summary'].previous_financed;
      delete this.cardsData['summary'].previous_service_amount;
      delete this.cardsData['summary'].previous_downPayment_amount;
    }
    
    // Recreate chart without previous data
    this.createMainChart();
  }

  private updateTableHeaders(): void {
    const unit = this.dateRange?.unit || 'day';
    const dateLabel = this.getDateLabel(unit);
    
    // Format date ranges for current and previous periods
    const currentPeriodLabel = this.dateRange?.start_date && this.dateRange?.end_date
      ? this.formatDateRangeByUnit(this.dateRange.start_date, this.dateRange.end_date, unit)
      : '';
    
    const previousPeriodLabel = this.dateRange?.previous_start_date && this.dateRange?.previous_end_date
      ? this.formatDateRangeByUnit(this.dateRange.previous_start_date, this.dateRange.previous_end_date, unit)
      : '';

    const headers: TableHeader[] = [
      { label: dateLabel, key: 'start', align: 'text-left', noTransform: true },
      { label: `Current${currentPeriodLabel ? ` (${currentPeriodLabel})` : ''}`, key: 'total_contracts', align: 'text-left', showProgress: true, showTooltip: true }
    ];

    // Only add previous column if previous dates are selected
    if (this.dateRange?.previous_start_date && this.dateRange?.previous_end_date) {
      headers.push({
        label: `Previous${previousPeriodLabel ? ` (${previousPeriodLabel})` : ''}`,
        key: 'total_contracts',
        align: 'text-left',
        isPrevious: true,
        showTooltip: true
      });
    }

    this.tableHeaders = headers;

    // Update cardsData with new table headers
    this.cardsData['tableHeaders'] = this.tableHeaders;
  }

  private getDateLabel(unit: TimeUnit): string {
    switch (unit) {
      case 'day': return 'Date';
      case 'week': return 'Week';
      case 'month': return 'Month';
      case 'quarter': return 'Quarter';
      case 'year': return 'Year';
      default: return 'Period';
    }
  }

  private formatDateRangeByUnit(startDate: string, endDate: string, unit: TimeUnit): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    
    switch (unit) {
      case 'day':
        return start.isSame(end, 'day') 
          ? start.format('MMM D, YYYY')
          : `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
      case 'week':
        return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
      case 'month':
        return start.isSame(end, 'month')
          ? start.format('MMM YYYY')
          : `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
      case 'quarter':
        return `Q${start.quarter()} ${start.year()}`;
      case 'year':
        return start.format('YYYY');
      default:
        return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
    }
  }

  onToggle(event: any) {
    if (event === 'table' || event === 'graph') {
      this.viewSignal.set(event === 'table' ? 'table' : 'chart');
      return;
    }

    let value: boolean | undefined;
    if (typeof event === 'boolean') value = event;
    else if (event && typeof event === 'object') {
      value = (event.value ?? event.detail ?? event.payload ?? event.checked) as any;
      if (typeof value !== 'boolean') {
        if ('isTable' in event) value = !!event.isTable;
        else value = event === 'table' ? true : undefined;
      }
    }

    this.viewSignal.set(value ? 'table' : 'chart');
  }

  viewChanged(event: any) {
    this.onToggle(event);
  }

  onCellHover(event: any) {
    if (event && event.row && event.event) {
      this.hoveredRowData = event.row;
      this.tooltipVisible = true;
      
      // Position tooltip relative to the table container
      const mouseEvent = event.event as MouseEvent;
      const target = mouseEvent.currentTarget as HTMLElement;
      const tableContainer = target.closest('.overflow-hidden');
      
      if (tableContainer) {
        const rect = tableContainer.getBoundingClientRect();
        const relativeX = mouseEvent.clientX - rect.left;
        const relativeY = mouseEvent.clientY - rect.top;
        
        // Estimate tooltip height (approximate based on content)
        const estimatedTooltipHeight = 150; // Approximate height for 6 items
        const estimatedTooltipWidth = 170; // Approximate width
        
        // Default position: to the right and above cursor
        let x = relativeX + 25;
        let y = relativeY - 30;
        
        // Check if tooltip would go off the bottom
        if (y + estimatedTooltipHeight > rect.height) {
          // Position above the cursor with more offset
          y = relativeY - estimatedTooltipHeight - 10;
        }
        
        // If still would go off top, position it at the top with margin
        if (y < 10) {
          y = 10;
        }
        
        // Check if tooltip would go off the right edge
        if (x + estimatedTooltipWidth > rect.width) {
          // Position to the left of cursor
          x = relativeX - estimatedTooltipWidth - 25;
        }
        
        // Ensure minimum left margin
        if (x < 10) {
          x = 10;
        }
        
        this.tooltipPosition = { x, y };
      } else {
        // Fallback
        this.tooltipPosition = {
          x: mouseEvent.offsetX + 25,
          y: mouseEvent.offsetY - 30
        };
      }
    } else {
      this.tooltipVisible = false;
      this.hoveredRowData = null;
    }
  }

  // Format table dates based on unit to show date ranges
  private formatTableDates(data: any[], unit?: string): any[] {
    if (!unit) return data;

    return data.map(row => {
      // If row has both start and end dates, format them as a range
      if (row.start && row.end) {
        // Use UTC to prevent timezone shifting
        const start = dayjs.utc(row.start);
        const end = dayjs.utc(row.end);
        let formattedStart = '';

        switch (unit) {
          case 'hour':
            // For hourly: show date with hour range
            if (start.isSame(end, 'day')) {
              formattedStart = `${start.format('MMM D')} (${start.format('HH:mm')} - ${end.format('HH:mm')})`;
            } else {
              formattedStart = `${start.format('MMM D HH:mm')} - ${end.format('MMM D HH:mm')}`;
            }
            break;
          
          case 'day':
            // For daily: just show the date without year
            formattedStart = start.format('MMM D');
            break;
          
          case 'week':
            // For weekly: show "14 Oct - 19 Oct" format
            if (start.isSame(end, 'month')) {
              formattedStart = `${start.format('DD MMM')} - ${end.format('DD MMM')}`;
            } else {
              formattedStart = `${start.format('DD MMM')} - ${end.format('DD MMM')}`;
            }
            break;
          
          case 'month':
            // For monthly: show month and year
            formattedStart = start.format('MMM YYYY');
            break;
          
          case 'quarter':
            // For quarterly: show "Jan 24 - Mar 24" format (start month - end month with year)
            formattedStart = `${start.format('MMM YY')} - ${end.format('MMM YY')}`;
            break;
          
          case 'year':
            // For yearly: show year
            formattedStart = start.format('YYYY');
            break;
          
          default:
            formattedStart = start.format('MMM D');
        }

        return {
          ...row,
          start: formattedStart
        };
      }

      return row;
    });
  }

}
